/**
 * Keyboard/mouse intent source.
 *
 * Design decisions (frozen by the runtime contract):
 * - Input emits INTENTS, never net messages: DOM handlers only mutate state, and the loop calls
 *   `poll()` which nothing else can call. `onInteract` / `onSelectSlot` are hooks for the block
 *   interaction and hotbar, not a second transport.
 * - One sample per message at exactly 10 Hz, driven by a frame-rate accumulator: the contract allows
 *   a single `input` frame carrying the CURRENT intent, and the runtime keeps the latest sample as
 *   the command for every 20 Hz prediction tick in between, which is what a server that holds the
 *   last received command per player also does.
 * - The `move` field is a WORLD-space horizontal wish vector, because `@rogueworlds/sim` applies it
 *   straight to velocity and never re-derives it from `look`; `look` is absolute yaw/pitch. Rotation
 *   of the keyboard intent by yaw therefore happens here, once, on the client.
 * - `poll()` returns a REUSED sample object (no per-frame allocation). It is safe because every
 *   transport serializes the frame synchronously on `send`.
 * - Yaw/pitch convention matches the camera rig: `pitch > 0` looks up, `yaw = 0` faces -Z, and the
 *   camera is oriented with `rotation.set(pitch, yaw, 0, "YXZ")` so the raycast direction computed
 *   here and the camera's view direction are the same vector.
 */

import { ACTION_JUMP, ACTION_SPRINT } from "@rogueworlds/sim";
import type { Vec3 } from "@rogueworlds/shared";

/** Input frames per second. */
export const INPUT_HZ = 10;
const SAMPLE_INTERVAL_S = 1 / INPUT_HZ;
/** Pitch clamp: 90 degrees minus a hair, so the view direction never degenerates. */
const PITCH_LIMIT_RAD = Math.PI / 2 - 0.01;
const MOUSE_SENSITIVITY = 0.0022;
/** Largest single-sample `dt` recorded (diagnostic only: the sim ignores `cmd.dt`). */
const MAX_SAMPLE_DT_S = 0.5;

/** A `ClientMessage`-shaped input sample (`{ t: "input", ... }`), ready to hand to `NetClient.send`. */
export interface InputSample {
  t: "input";
  seq: number;
  dt: number;
  move: Vec3;
  look: Vec3;
  actions: number;
}

export interface InputHandlers {
  /** Mouse press while pointer-locked. */
  readonly onInteract?: (button: "primary" | "secondary") => void;
  /** Digit 1..9 pressed, `index` is 0-based. */
  readonly onSelectSlot?: (index: number) => void;
}

const DIGIT_CODES = [
  "Digit1",
  "Digit2",
  "Digit3",
  "Digit4",
  "Digit5",
  "Digit6",
  "Digit7",
  "Digit8",
  "Digit9",
] as const;

const CONSUMED_KEYS: ReadonlySet<string> = new Set([
  "KeyW",
  "KeyA",
  "KeyS",
  "KeyD",
  "Space",
  "ShiftLeft",
  "ShiftRight",
]);

/** Unit view direction for a yaw/pitch pair (matches the camera's YXZ orientation). */
export function lookDirection(yaw: number, pitch: number, out: Vec3): Vec3 {
  const cosPitch = Math.cos(pitch);
  out.x = -cosPitch * Math.sin(yaw);
  out.y = Math.sin(pitch);
  out.z = -cosPitch * Math.cos(yaw);
  return out;
}

export class InputController {
  private readonly canvas: HTMLCanvasElement;
  private readonly handlers: InputHandlers;
  private readonly keys = new Set<string>();
  private readonly sample: InputSample = {
    t: "input",
    seq: 0,
    dt: 0,
    move: { x: 0, y: 0, z: 0 },
    look: { x: 0, y: 0, z: 0 },
    actions: 0,
  };

  private yawValue = 0;
  private pitchValue = 0;
  private accumulator = 0;
  private seqCounter = 0;
  private locked = false;
  private attached = false;

  private readonly onCanvasClick = (): void => {
    this.requestPointerLock();
  };

  private readonly onCanvasMouseDown = (event: MouseEvent): void => {
    if (!this.locked) {
      return;
    }
    event.preventDefault();
    if (event.button === 0) {
      this.handlers.onInteract?.("primary");
    } else if (event.button === 2) {
      this.handlers.onInteract?.("secondary");
    }
  };

  private readonly onContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private readonly onPointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
    if (!this.locked) {
      this.clearKeys();
    }
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.locked) {
      return;
    }
    this.yawValue -= event.movementX * MOUSE_SENSITIVITY;
    this.pitchValue = clamp(this.pitchValue - event.movementY * MOUSE_SENSITIVITY, -PITCH_LIMIT_RAD, PITCH_LIMIT_RAD);
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const code = event.code;
    if (CONSUMED_KEYS.has(code)) {
      if (event.repeat) {
        event.preventDefault();
        return;
      }
      this.keys.add(code);
      event.preventDefault();
      return;
    }
    if (event.repeat) {
      return;
    }
    const slot = DIGIT_CODES.indexOf(code as (typeof DIGIT_CODES)[number]);
    if (slot >= 0) {
      this.handlers.onSelectSlot?.(slot);
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (CONSUMED_KEYS.has(event.code)) {
      this.keys.delete(event.code);
      event.preventDefault();
    }
  };

  private readonly onBlur = (): void => {
    this.clearKeys();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== "visible") {
      this.clearKeys();
    }
  };

  constructor(canvas: HTMLCanvasElement, handlers: InputHandlers = {}) {
    this.canvas = canvas;
    this.handlers = handlers;
  }

  get yaw(): number {
    return this.yawValue;
  }

  get pitch(): number {
    return this.pitchValue;
  }

  get isLocked(): boolean {
    return this.locked;
  }

  attach(): void {
    if (this.attached) {
      return;
    }
    this.attached = true;
    this.canvas.addEventListener("click", this.onCanvasClick);
    this.canvas.addEventListener("mousedown", this.onCanvasMouseDown);
    this.canvas.addEventListener("contextmenu", this.onContextMenu);
    document.addEventListener("pointerlockchange", this.onPointerLockChange);
    document.addEventListener("mousemove", this.onMouseMove);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.onBlur);
  }

  detach(): void {
    if (!this.attached) {
      return;
    }
    this.attached = false;
    this.canvas.removeEventListener("click", this.onCanvasClick);
    this.canvas.removeEventListener("mousedown", this.onCanvasMouseDown);
    this.canvas.removeEventListener("contextmenu", this.onContextMenu);
    document.removeEventListener("pointerlockchange", this.onPointerLockChange);
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.onBlur);
    this.clearKeys();
    if (document.pointerLockElement === this.canvas) {
      document.exitPointerLock();
    }
    this.locked = false;
  }

  /** Escape is handled by the browser (it exits pointer lock); this is the click-to-capture path. */
  requestPointerLock(): void {
    if (this.locked || document.pointerLockElement === this.canvas) {
      return;
    }
    const result: Promise<void> | undefined = this.canvas.requestPointerLock() as Promise<void> | undefined;
    if (result !== undefined && typeof result.catch === "function") {
      result.catch(() => {
        // A rejected capture (no user gesture, sandboxed iframe) is not fatal: the HUD hint stays up.
      });
    }
  }

  /**
   * Accumulates frame time and returns the reused sample once per 10 Hz interval, `undefined` in
   * between.
   */
  poll(dtSeconds: number): InputSample | undefined {
    this.accumulator += dtSeconds > MAX_SAMPLE_DT_S ? MAX_SAMPLE_DT_S : dtSeconds;
    if (this.accumulator < SAMPLE_INTERVAL_S) {
      return undefined;
    }
    const dt = this.accumulator;
    this.accumulator = 0;

    const sample = this.sample;
    this.seqCounter += 1;
    sample.seq = this.seqCounter;
    sample.dt = dt;

    const forward = (this.keys.has("KeyW") ? 1 : 0) - (this.keys.has("KeyS") ? 1 : 0);
    const strafe = (this.keys.has("KeyD") ? 1 : 0) - (this.keys.has("KeyA") ? 1 : 0);
    // World-space wish vector: forward = (-sin yaw, -cos yaw), right = (cos yaw, -sin yaw).
    const sinYaw = Math.sin(this.yawValue);
    const cosYaw = Math.cos(this.yawValue);
    sample.move.x = -sinYaw * forward + cosYaw * strafe;
    sample.move.y = 0;
    sample.move.z = -cosYaw * forward - sinYaw * strafe;
    sample.look.x = this.yawValue;
    sample.look.y = this.pitchValue;
    sample.look.z = 0;
    const jump = this.keys.has("Space") ? ACTION_JUMP : 0;
    const sprint = this.keys.has("ShiftLeft") || this.keys.has("ShiftRight") ? ACTION_SPRINT : 0;
    sample.actions = jump | sprint;
    return sample;
  }

  private clearKeys(): void {
    if (this.keys.size > 0) {
      this.keys.clear();
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
